import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { DateTimeHelperService } from '../../shared/date-time-helper/date-time-helper.service';
import { LicenceModules } from '../../viewmodels/LicenceModules';
import { Licence } from '../../viewmodels/licence';
import { BackendService } from '../backend.service';
import { ApiResult } from '../messages/api-result';
import { DisplayMessage } from '../messages/display-message';

@Injectable()
export class LicensingService {
  licence: BehaviorSubject<Licence> = new BehaviorSubject<Licence>(new Licence());

  constructor(private backendService: BackendService, private messageService: MessageService, private dateTimeHelperService: DateTimeHelperService) { }

  getCurrentLicence() {
    this.backendService
      .get<Licence>(Licence)
      .pipe(take(1))
      .subscribe(
        data => {
          this.licence.next(data);
        },
        (errorResult: ApiResult) => {
          if (errorResult) {
            errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage('Licensing', message.text, errorResult.severity)));
          }
        },
      );
  }

  getCurrentLicenceObservable(): Observable<void> {
    if (this.licence.value.loaded) {
      return of(null);
    }

    return this.backendService.get<Licence>(Licence).pipe(
      map(data => {
        this.licence.next(data);
      }),
      catchError((errorResult: ApiResult) => {
        if (errorResult) {
          errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage('Licensing', message.text, errorResult.severity)));
        }
        return of(null);
      }),
    );
  }

  unloadCurrentLicence() {
    this.licence.next(new Licence());
  }

  installLicence(customerId: string) {
    const newLicence = new Licence();
    newLicence.customerId = customerId;

    this.backendService
      .create<Licence>(Licence, newLicence)
      .pipe(take(1))
      .subscribe(
        data => {
          this.licence.next(data);
        },
        (errorResult: ApiResult) => {
          if (errorResult) {
            errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage('Licensing', message.text, errorResult.severity)));
          }
        },
      );
  }

  updateLicence() {
    this.licence.pipe(take(1)).subscribe(lic => {
      if (!lic) {
        return;
      }

      lic.licenceData = '';
      this.backendService
        .update<Licence>(Licence, lic.customerId, lic)
        .pipe(take(1))
        .subscribe(
          data => {
            this.licence.next(data);
          },
          (errorResult: ApiResult) => {
            if (errorResult) {
              errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage('Licensing', message.text, errorResult.severity)));
            }
          },
        );
    });
  }

  uploadLicence(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const uploadLicence = new Licence();
      uploadLicence.licenceData = reader.result.toString();
      this.backendService
        .update<Licence>(Licence, '0', uploadLicence)
        .pipe(take(1))
        .subscribe(
          data => {
            this.licence.next(data);
          },
          (errorResult: ApiResult) => {
            if (errorResult) {
              errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage('Licensing', message.text, errorResult.severity)));
            }
          },
        );
    };
    reader.readAsText(file);
  }

  removeLicence() {
    this.licence.pipe(take(1)).subscribe(lic => {
      this.backendService
        .delete<Licence>(Licence, lic.customerId)
        .pipe(take(1))
        .subscribe(
          data => {
            const deleted: Licence = new Licence();
            deleted.loadFromJson(data);
            this.licence.next(deleted);
          },
          (errorResult: ApiResult) => {
            if (errorResult) {
              errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage('Licensing', message.text, errorResult.severity)));
            }
          },
        );
    });
  }
  
  getLicenseExpiryDays(): number {
    const licenseExpiryDate = this.dateTimeHelperService.stringToMoment(this.licence.value.expiryDate);
    const currentDate = moment();
    return licenseExpiryDate.diff(currentDate, 'days');
  }
  
  isModuleLicenced(requiredModule: LicenceModules): boolean {
    // eslint-disable-next-line no-bitwise
    return (this.licence.value.licencedModules & requiredModule) as any;
  }
}
